import BackendAuthHandler from 'BackendAuthHandler';
import TokenData from 'TokenData';
import {
  AdvisorAccess,
  AdvisorInvitation,
  CreateAdvisorInvitationRequest,
  GetAdvisorAccessResponse,
  GetAdvisorsResponse,
} from '../../models/Advisor';
import { Article, ArticleContent } from '../../models/Article';
import Contact from '../../models/Contact';
import Document from '../../models/Document';
import FundOpportunity from '../../models/FundOpportunity';
import {
  CreateInvestmentEntityRequest,
  InvestmentEntity,
  InvestorAllocationsResponse,
} from '../../models/InvestmentEntity';
import Investor from '../../models/Investor';
import BaseBackendService, { NOT_FOUND, OpportunityStatus } from '../BaseBackendService';
import {
  parseAdvisorInvitationsFromResponse,
  transformAdvisorInvitation,
} from './backendServiceAdvisorUtils';
import {
  mapOwnContactResponse,
  mapProfileUpdateResponse,
  parseChangePasswordResponse,
  ProfileUpdatePayload,
  ProfileUpdateResult,
  OwnContactResult,
  ChangePasswordResult,
  rethrowOrWrapChangePasswordError,
  rethrowOrWrapProfileUpdateError,
  getProfileUpdateReauthError,
} from './backendServiceProfileUtils';
import {
  fetchVerifiedGlobalConfiguration,
  rethrowOrWrapCustodianStatusError,
  verifyCustodianStatusPayload,
} from './backendServiceSignedConfigUtils';
import { fetchFundOpportunitiesByStatuses } from './backendServiceContactUtils';
import type { NonAdminBackendServiceExtensions } from './nonAdminBackendServiceExtensions';

// Intentional interface/class declaration merging: the interface declares the admin-only
// extension methods (composed onto the class) so callers see them as typed instance methods.
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
export interface BackendService extends NonAdminBackendServiceExtensions {}

export class BackendService extends BaseBackendService {
  private contactMePost<T>(path: string, body?: unknown): Promise<T> {
    return this.authApiMutateAuthed(`contacts/me/${path}`, 'POST', body, (json) => json as T);
  }

  getInvestorAllocations(
    investorId: string,
    includePlaceholder: boolean,
  ): Promise<InvestorAllocationsResponse> {
    return this.authApiGetAuthed(
      `contacts/investments/${investorId}/allocations?includePlaceholder=${includePlaceholder}`,
      'Unable to fetch investor allocations',
      (json) => json.data || json,
    );
  }

  getInvestorById(id: string): Promise<Investor> {
    return this.authApiGetAuthed(
      `contacts/investments/${id}`,
      'Network error while fetching investor',
      (data) => data.data as Investor,
    );
  }

  getUserInitials(): Promise<{ initials: string }> {
    return this.authApiGetAuthed(
      'contacts/userInitials',
      'Network error while fetching user initials',
      (json) => json,
    );
  }

  getInvestmentsAndOpportunities(): Promise<Investor[]> {
    return this.authApiGetAuthed(
      'contacts/investments',
      'Network error while fetching investments',
      (json) => json.data as Investor[],
    );
  }

  setLastUpdateViewed(fundOpportunityId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'contacts/investments/lastUpdateViewed',
      'PUT',
      { fundOpportunityId },
      () => undefined,
    );
  }

  getContentManagement(modelType: string, modelId: string, token: string): Promise<string | null> {
    return this.getAsset(`contentManagement/${modelType}/${modelId}/published.json`, token, {
      url: false,
    })
      .then((content) => content as string)
      .catch((error: any) => (error == NOT_FOUND ? null : Promise.reject(error)));
  }

  retrieveInvestorDocuments(investor: Investor): Promise<Document[]> {
    return this.authApiGetAuthed(
      `contacts/investments/${investor.id}/documents`,
      'Network error while fetching investor documents',
      (json) => json.data as Document[],
    );
  }

  getFundOpportunityByStatus(
    status: OpportunityStatus | OpportunityStatus[],
  ): Promise<FundOpportunity[]> {
    return this.authFetchPromise((token) =>
      fetchFundOpportunitiesByStatuses([status].flat(), async (s) => {
        const response = await this.authenticatedFetch(
          this.apiURL(`fundOpportunities/byStatus/${s}`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return (await response.json()).data as FundOpportunity[];
      }),
    );
  }

  getFundOpportunityContent(
    type: 'FundOpportunity' | 'FundUpdate',
    fundOpportunityId: string,
    _draft: boolean = false,
  ): Promise<string | null> {
    return this.setupAssetStorageWithRetry(false).then((token) =>
      this.getContentManagement(type, fundOpportunityId, token.token.token as string),
    );
  }

  getPublishedArticles(): Promise<Article[]> {
    return this.authApiGet('articles', (json) => json.data as Article[]);
  }

  getArticleContent(article: Article): Promise<ArticleContent> {
    return this.setupAssetStorageWithRetry(false)
      .then((token: TokenData) =>
        this.getAsset(article.resultLocation!, token.token.token, { url: false }),
      )
      .then((content) => JSON.parse(content as string) as ArticleContent);
  }

  getGlobalConfiguration(): Promise<
    import('../../models/GlobalConfiguration').GlobalConfiguration
  > {
    return fetchVerifiedGlobalConfiguration({
      withAuthToken: () => new BackendAuthHandler().withAuthToken(),
      authenticatedFetch: (url, init) => this.authenticatedFetch(url, init),
      apiURL: (path) => this.apiURL(path),
      verifySignedConfigData: (d, s, k) => this.verifySignedConfigData(d, s, k),
    });
  }

  getAdvisorInvitations(includePending?: boolean): Promise<AdvisorInvitation[]> {
    const path = includePending ? 'advisors?includePending=true' : 'advisors';
    return this.authApiGetAuthed(
      path,
      'Network error while fetching advisor invitations',
      parseAdvisorInvitationsFromResponse,
    );
  }

  getInvestmentEntitiesForUser(): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      'investment-entities',
      'Unable to retrieve investment entities',
      (json) => json.data || [],
    );
  }

  createInvestmentEntity(
    entityData: CreateInvestmentEntityRequest,
    investor: Investor,
  ): Promise<InvestmentEntity> {
    return this.authApiPostData(
      `contacts/investments/${investor.id}/investment-entity`,
      entityData,
      'Network error while creating investment entity',
      true,
    );
  }

  searchContacts(query: string): Promise<Contact[]> {
    return this.authApiGetAuthed(
      `investment-entities/contacts/search?term=${encodeURIComponent(query)}`,
      'Failed to search contacts',
      (json) => json.data || [],
    );
  }

  async getAdvisorAccess(): Promise<AdvisorAccess[]> {
    return this.authApiGetAuthed(
      'advisors/access',
      'Network error while fetching advisor access',
      (json: GetAdvisorAccessResponse) => json.data,
    );
  }

  async setAdviseForContact(contactId: string): Promise<{
    success: boolean;
    message: string;
    data: {
      advisorContactId: string;
      advisorContactName: string;
      targetContactId: string;
      targetContactName: string;
      role: string;
    };
  }> {
    return this.authApiMutateAuthed(`adviseFor/${contactId}`, 'POST', undefined, (json) => json);
  }

  async getAdvisors(): Promise<AdvisorInvitation[]> {
    return this.authApiGetAuthed(
      'advisors?includePending=true',
      'Network error while fetching advisors',
      (json: GetAdvisorsResponse) => json.data.map(transformAdvisorInvitation),
    );
  }

  async createAdvisorInvitation(
    invitationData: CreateAdvisorInvitationRequest,
  ): Promise<AdvisorInvitation> {
    return this.authApiPostData(
      'advisors',
      invitationData,
      'Network error while creating advisor invitation',
      true,
    );
  }

  async getCustodianStatus(): Promise<{
    isCustodian: boolean;
    contactId: string;
    timestamp: string;
  }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('contacts/custodian-status'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw this.handleError(await response.json());
      }
      const json = await response.json();
      const { data, signature, publicKeyBase64 } = json;
      return verifyCustodianStatusPayload(
        data,
        signature,
        publicKeyBase64,
        (d, s, k) => this.verifySignedConfigData(d, s, k),
        (body) => this.handleError(body),
      );
    }).catch((error) => rethrowOrWrapCustodianStatusError(error, (body) => this.handleError(body)));
  }

  async regenerateAdvisorInvitation(invitationId: string): Promise<AdvisorInvitation> {
    return this.authApiPostData(
      `advisors/invitation/regenerate/${invitationId}`,
      {},
      'Network error while regenerating advisor invitation',
      true,
    );
  }

  async cancelAdvisorInvitation(invitationId: string): Promise<{ id: string; status: string }> {
    return this.authApiMutateAuthed(
      `advisors/invitation/${invitationId}`,
      'DELETE',
      undefined,
      (json) => json.data,
    );
  }

  async removeAdvisor(
    advisorId: string,
  ): Promise<{ id: string; active: boolean; message: string }> {
    return this.authApiMutateAuthed(
      `advisors/${advisorId}`,
      'DELETE',
      undefined,
      (json) => json.data,
    );
  }

  async updateOwnProfile(profileData: ProfileUpdatePayload): Promise<ProfileUpdateResult> {
    return this.authFetchPromise(async (token) => {
      if (!token) {
        throw 'Authentication required';
      }
      const response = await this.authenticatedFetch(this.apiURL('contacts/me'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });
      if (!response.ok) {
        const errorBody = await response.json();
        const reauthError = getProfileUpdateReauthError(response, errorBody);
        if (reauthError) {
          throw reauthError;
        }
        throw this.handleError(errorBody);
      }
      return mapProfileUpdateResponse(await response.json());
    }).catch((error) => rethrowOrWrapProfileUpdateError(error, (body) => this.handleError(body)));
  }

  async changePassword(reauthCode?: string): Promise<ChangePasswordResult> {
    return this.authFetchPromise(async (token) => {
      if (!token) {
        throw 'Authentication required';
      }
      const response = await this.authenticatedFetch(this.apiURL('contacts/me/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(reauthCode ? { reauthCode } : {}),
      });
      return parseChangePasswordResponse(response, await response.json());
    }).catch((error) => rethrowOrWrapChangePasswordError(error, (body) => this.handleError(body)));
  }

  cancelEmailChange(): Promise<{ success: boolean; message: string }> {
    return this.contactMePost('cancel-email-change');
  }

  resendMobileVerificationCode(): Promise<{
    success: boolean;
    message: string;
    mobileVerificationExpiry?: number;
  }> {
    return this.contactMePost('resend-mobile-verification-code');
  }

  resendEmailChangeConfirmation(): Promise<{
    success: boolean;
    message: string;
    emailChangeExpiry?: string | number;
  }> {
    return this.contactMePost('regenerate-email-change-code');
  }

  verifyEmailChangeCode(emailChangeCode: string): Promise<{ success: boolean; message: string }> {
    return this.contactMePost('verify-email-change-code', { emailChangeCode });
  }

  getOwnContact(): Promise<OwnContactResult> {
    return this.authApiGetAuthed(
      'contacts/me',
      'Network error while fetching contact data',
      mapOwnContactResponse,
    );
  }

  acceptReferralInvitation(
    code: string,
    data: {
      password: string;
      mobile: string;
      firstName?: string;
      lastName?: string;
      eoiAmount?: number;
    },
  ): Promise<{ status: string; message: string }> {
    return this.authApiPostData(
      `referral/invitation/${code}/accept`,
      data,
      'Network error while accepting referral invitation',
    );
  }

  rejectReferralInvitation(code: string): Promise<{ status: string; message: string }> {
    return this.authApiPostData(
      `referral/invitation/${code}/reject`,
      {},
      'Network error while rejecting referral invitation',
    );
  }
}

export default BackendService;
